import {
  Input,
  Inject,
  OnInit,
  OnChanges,
  OnDestroy,
  Component,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbstractControl, FormBuilder, UntypedFormGroup } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';

import { Subscription, filter, tap, catchError, of, finalize } from 'rxjs';

import { Location } from '@shared/location/models';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { StripeService } from '@tablesready/api-client';

@Component({
  selector: 'tablesready-stripe-integration',
  templateUrl: './stripe.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StripeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() disabled: boolean = true;
  icon = AppIcon;
  size = AppIconSize;
  location: Location;
  sending = false;
  form: UntypedFormGroup = null;
  connected = false;
  serviceFee: number;
  errorMessage = '';

  private subscriptions = new Subscription();

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly alertsService: AlertsService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly stripeService: StripeService,
    @Inject(APP_VARIABLES_TOKEN) public readonly appVariables: AppVariables,
  ) {
    this.serviceFee = this.appVariables.SERVICE_FEE;
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      enabled: [{ value: false, disabled: this.disabled }],
    });

    this.subscriptions.add(
      this.authRestaurantService.select()
        .pipe(
          filter(user => user.location !== undefined),
          tap(({location}) => {
            this.location = location;
            this.connected = location.integrations?.stripe?.accountId && location.integrations?.stripe.state === null;
            this.form.patchValue({
              enabled: location.integrations?.stripe?.enabled,
            });
            this.cdr.markForCheck();
          }),
        )
        .subscribe(),
    );

    const { queryParamMap } = this.activatedRoute.snapshot;
    const state = queryParamMap.get('state');
    if (state) {
      this.stripeService.complete({state})
        .pipe(
          tap(() => this.router.navigateByUrl('/settings/preferences/integrations')),
          catchError(error => {
            if (error instanceof HttpErrorResponse) {
              if (error.status === 422) {
                this.errorMessage = error.error.message;
                this.cdr.markForCheck();
              } else {
                this.alertsService.addError(error.error.message);
              }
            }

            return of(null);
          }),
          finalize(() => this.sending = false),
        )
        .subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.disabled?.previousValue !== undefined) {
      this.enabledControl.setValue(this.enabledControl.value, { disabled: changes.disabled.currentValue });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get enabledControl(): AbstractControl {
    return this.form.get('enabled');
  }

  authorize() {
    this.sending = true;
    this.stripeService.authorize()
      .pipe(
        tap(() => this.errorMessage = ''),
        tap(({url}) => window.location.href = url),
        catchError(error => {
          return of(null);
        }),
        finalize(() => this.sending = false),
      )
      .subscribe();
  }

  save() {
    this.sending = true;
    this.stripeService.updateIntegration(this.form.value)
      .pipe(
        tap(() => {
          this.alertsService.addSuccess('Your Stripe Connect successfully updated.', 'Updated: ');
          this.form.markAsPristine();
        }),
        catchError(() => {
          this.alertsService.addError('Saving failed.');

          return of(null);
        }),
        finalize(() => this.sending = false),
      )
      .subscribe();
  }
}
